import type { DataCoverage, DriverState, DriverTiming, RaceEvent, RaceSnapshot, TrackStatus } from './race';

/** Versioned display policy. These thresholds describe grouping, never DRS eligibility. */
export const GAP_POLICY = { version: 1, joinSeconds: 1.2, leaveSeconds: 1.6, confirmMs: 3_000, windowMs: 30_000, minimumSpanMs: 10_000, minimumSamples: 5, flatSeconds: .15, staleMs: 15_000 } as const;
export interface GapLink { key: string; front: number; rear: number; seconds: number; joined: boolean; candidateSince: number | null }
export interface GapGrouping { atMs: number; sessionKey: string; links: GapLink[]; groups: number[][] }
export const numericGap = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
const blocked = (status: TrackStatus) => status !== 'GREEN' && status !== 'YELLOW';
function latestEvents(events: RaceEvent[], displayMs: number) {
  const latest = new Map<string, RaceEvent>();
  for (const event of events) latest.set(event.id, event);
  return [...latest.values()].filter(event => event.atMs <= displayMs).sort((a, b) => a.atMs - b.atMs);
}
function sameLap(front: Pick<DriverTiming, 'lap' | 'completedLaps'>, rear: Pick<DriverTiming, 'lap' | 'completedLaps'>) {
  return (front.completedLaps ?? front.lap) === (rear.completedLaps ?? rear.lap);
}
function inGap(coverage: DataCoverage | undefined, atMs: number) { return coverage?.gaps.some(gap => atMs >= gap.fromMs && atMs <= gap.toMs) ?? false; }

/** Hysteresis is carried by pair identity; discontinuous display seeks discard it. */
export function groupCars(snapshot: RaceSnapshot, previous?: GapGrouping): GapGrouping {
  const drivers = snapshot.drivers.filter(driver => driver.position !== null).sort((a, b) => a.position! - b.position!);
  const continuous = previous?.sessionKey === snapshot.session.key && snapshot.atMs >= previous.atMs && snapshot.atMs - previous.atMs <= 5_000;
  const known = new Map((continuous ? previous!.links : []).map(link => [link.key, link]));
  const links: GapLink[] = [];
  const groups: number[][] = [];
  for (let index = 0; index < drivers.length; index++) {
    const rear = drivers[index], front = drivers[index - 1];
    const seconds = numericGap(rear.interval);
    const valid = front && seconds !== null && rear.position === front.position! + 1 && sameLap(front, rear)
      && front.status === 'RUNNING' && rear.status === 'RUNNING' && numericGap(rear.gapToLeader) !== null && !blocked(snapshot.trackStatus) && !inGap(snapshot.coverage, snapshot.atMs);
    if (!valid) { groups.push([rear.number]); continue; }
    const key = `${front.number}:${rear.number}`;
    const old = known.get(key);
    let joined = old?.joined ?? false;
    const changing = joined ? seconds > GAP_POLICY.leaveSeconds : seconds <= GAP_POLICY.joinSeconds;
    let candidateSince = changing ? old?.candidateSince ?? snapshot.atMs : null;
    if (candidateSince !== null && snapshot.atMs - candidateSince >= GAP_POLICY.confirmMs) { joined = !joined; candidateSince = null; }
    links.push({ key, front: front.number, rear: rear.number, seconds, joined, candidateSince });
    if (joined && groups.length) groups[groups.length - 1].push(rear.number); else groups.push([rear.number]);
  }
  return { atMs: snapshot.atMs, sessionKey: snapshot.session.key, links, groups };
}

export interface GapSample { atMs: number; seconds: number; eventId: string }
export interface PairTrend { samples: GapSample[]; state: 'approaching' | 'opening' | 'flat' | 'building' | 'unavailable'; delta: number | null; reason: string }
/** Only independent, explicitly reported interval observations are samples. No copied-value ticks. */
export function pairTrend(events: RaceEvent[], frontNumber: number, rearNumber: number, displayMs: number, coverage?: DataCoverage): PairTrend {
  const unavailable = (reason: string): PairTrend => ({ samples: [], state: 'unavailable', delta: null, reason });
  if (frontNumber === rearNumber) return unavailable('选择两位不同车手');
  if (inGap(coverage, displayMs)) return unavailable('此时段采集存在缺口');
  const state = new Map<number, Partial<DriverTiming>>();
  let flag: TrackStatus = 'UNKNOWN';
  let samples: GapSample[] = [];
  let previousValid = false;
  let previousPositions = '';
  const sourceTimes = new Map<string, GapSample>();
  const ordered = latestEvents(events, displayMs);
  const windowStart = displayMs - GAP_POLICY.windowMs;
  let gapBoundary = Math.max(0, coverage?.startMs ?? 0);
  for (const gap of coverage?.gaps ?? []) if (gap.toMs <= displayMs) gapBoundary = Math.max(gapBoundary, gap.toMs);
  for (let index = 0; index < ordered.length;) {
    const time = ordered[index].atMs;
    const batch: RaceEvent[] = [];
    while (index < ordered.length && ordered[index].atMs === time) batch.push(ordered[index++]);
    let observation: Extract<RaceEvent, { type: 'timing' }> | undefined;
    let reset = false;
    for (const event of batch) {
      if (event.type === 'status') { if (event.status !== flag && (blocked(event.status) || blocked(flag))) reset = true; flag = event.status; }
      if (event.type === 'pit' && [frontNumber, rearNumber].includes(event.stop.driverNumber)) reset = true;
      if (event.type !== 'timing' || ![frontNumber, rearNumber].includes(event.driverNumber)) continue;
      const old = state.get(event.driverNumber) ?? {};
      if (event.patch.status !== undefined && event.patch.status !== old.status) reset = true;
      state.set(event.driverNumber, { ...old, ...event.patch });
      if (event.driverNumber === rearNumber && event.patch.interval !== undefined && !/^(seed|snapshot|initial):/.test(event.id)) observation = event;
    }
    const front = state.get(frontNumber), rear = state.get(rearNumber);
    const positions = `${front?.position}:${rear?.position}`;
    const valid = Boolean(front && rear && front.position != null && rear.position === front.position + 1 && front.status === 'RUNNING' && rear.status === 'RUNNING'
      && sameLap(front as DriverTiming, rear as DriverTiming) && numericGap(rear.gapToLeader) !== null && numericGap(rear.interval) !== null && !blocked(flag) && time >= gapBoundary && !inGap(coverage, time));
    if (reset || !valid || !previousValid || positions !== previousPositions) samples = [];
    previousValid = valid;
    previousPositions = positions;
    if (!observation) continue;
    // Snapshot receive time is not an independent source observation.
    const identity = observation.patch.intervalUpdatedAt;
    if (!identity || !Number.isFinite(Date.parse(identity))) continue;
    const value = numericGap(observation.patch.interval);
    if (value === null) continue;
    const existing = sourceTimes.get(identity);
    if (existing) { existing.seconds = value; existing.eventId = observation.id; continue; }
    const sample = { atMs: time, seconds: value, eventId: observation.id };
    sourceTimes.set(identity, sample);
    if (valid && time >= windowStart) samples.push(sample);
  }
  if (!previousValid) return unavailable('等待相邻关系、各车圈号和赛道状态对齐');
  samples = samples.filter(sample => sample.atMs >= Math.max(windowStart, gapBoundary));
  if (!samples.length || displayMs - samples[samples.length - 1].atMs > GAP_POLICY.staleMs) return unavailable('近期没有可验证的间隔观测');
  if (samples.length < GAP_POLICY.minimumSamples || samples[samples.length - 1].atMs - samples[0].atMs < GAP_POLICY.minimumSpanMs) return { samples, state: 'building', delta: null, reason: '趋势待积累' };
  const delta = samples[samples.length - 1].seconds - samples[0].seconds;
  return { samples, state: Math.abs(delta) <= GAP_POLICY.flatSeconds ? 'flat' : delta < 0 ? 'approaching' : 'opening', delta, reason: '连续有效观测内的首末间隔变化' };
}

export interface ReviewFact { id: string; atMs: number; kind: 'flag' | 'position' | 'pit' | 'tyre'; label: string; detail: string; driverNumber?: number; status?: TrackStatus; priority: number; sourceIds: string[] }
const flagLabels: Record<TrackStatus, string> = { GREEN: '绿旗', YELLOW: '黄旗', RED: '红旗', SC: '安全车', VSC: '虚拟安全车', FINISHED: '结束', UNKNOWN: '状态未知' };

/** Facts retain their source event IDs; chronology is not an attribution of cause. */
export function reviewFacts(events: RaceEvent[], displayMs: number, coverage?: DataCoverage): ReviewFact[] {
  const positions = new Map<number, number>();
  const tyres = new Map<number, string>();
  const stints = new Map<number, number>();
  const facts: ReviewFact[] = [];
  let flag: TrackStatus | undefined;
  let lastTime = -1;
  const seen = new Set<string>();
  for (const event of latestEvents(events, displayMs)) {
    if (seen.has(event.id)) continue;
    seen.add(event.id);
    if (coverage?.gaps.some(gap => gap.toMs > lastTime && gap.toMs <= event.atMs)) { positions.clear(); tyres.clear(); stints.clear(); flag = undefined; }
    lastTime = event.atMs;
    if (inGap(coverage, event.atMs)) continue;
    if (event.type === 'status') {
      if (event.status !== flag && event.status !== 'UNKNOWN') facts.push({ id: event.id, atMs: event.atMs, kind: 'flag', label: flagLabels[event.status], detail: flag === undefined ? '此时起有已记录的赛道状态' : `${flagLabels[flag]} → ${flagLabels[event.status]}`, status: event.status, priority: event.status === 'RED' ? 100 : event.status === 'SC' ? 90 : event.status === 'VSC' ? 80 : 25, sourceIds: [event.id] });
      flag = event.status;
    } else if (event.type === 'pit') {
      facts.push({ id: event.id, atMs: event.atMs, kind: 'pit', label: '维修区通行', detail: `第 ${event.stop.lap || '—'} 圈 · ${event.stop.laneDuration === null ? '用时未知' : `${event.stop.laneDuration.toFixed(3)} 秒`}`, driverNumber: event.stop.driverNumber, priority: 40, sourceIds: [event.id] });
    } else if (event.type === 'timing') {
      if (event.patch.position != null) {
        const old = positions.get(event.driverNumber);
        if (old !== undefined && old !== event.patch.position) facts.push({ id: `${event.id}:position`, atMs: event.atMs, kind: 'position', label: `P${old} → P${event.patch.position}`, detail: '计时排名变化；不直接等同于赛道超车', driverNumber: event.driverNumber, priority: Math.min(old, event.patch.position) === 1 ? 75 : 30 + Math.min(15, Math.abs(old - event.patch.position)), sourceIds: [event.id] });
        positions.set(event.driverNumber, event.patch.position);
      }
      if (event.patch.compound !== undefined && event.patch.compound !== 'UNKNOWN') {
        const old = tyres.get(event.driverNumber);
        if (old !== undefined && old !== event.patch.compound) facts.push({ id: `${event.id}:tyre`, atMs: event.atMs, kind: 'tyre', label: `${old} → ${event.patch.compound}`, detail: '记录到轮胎配方变化', driverNumber: event.driverNumber, priority: 20, sourceIds: [event.id] });
        tyres.set(event.driverNumber, event.patch.compound);
      }
      if (event.patch.stints) {
        const old = stints.get(event.driverNumber);
        const latest = event.patch.stints.at(-1);
        if (old !== undefined && old > 0 && event.patch.stints.length > old && latest && latest.startLapKnown !== false && latest.compound !== 'UNKNOWN' && !facts.some(fact => fact.id === `${event.id}:tyre`)) facts.push({ id: `${event.id}:tyre`, atMs: event.atMs, kind: 'tyre', label: `新轮胎段 · ${latest.compound}`, detail: `第 ${latest.startLap} 圈起记录到新轮胎段`, driverNumber: event.driverNumber, priority: 20, sourceIds: [event.id] });
        stints.set(event.driverNumber, event.patch.stints.length);
      }
    }
  }
  return facts;
}

export function keyMoments(facts: ReviewFact[]): ReviewFact[] {
  const selected: ReviewFact[] = [];
  for (const fact of [...facts].sort((a, b) => b.priority - a.priority || a.atMs - b.atMs)) {
    if (selected.some(other => Math.abs(other.atMs - fact.atMs) < 45_000)) continue;
    selected.push(fact);
    if (selected.length === 3) break;
  }
  return selected.sort((a, b) => a.atMs - b.atMs);
}

/** State spans stop at coverage gaps; restoring a state requires a new recorded status. */
export function flagIntervals(facts: ReviewFact[], displayMs: number, coverage?: DataCoverage) {
  const flags = facts.filter(fact => fact.kind === 'flag' && fact.status && fact.atMs <= displayMs).sort((a,b) => a.atMs - b.atMs);
  return flags.flatMap((fact, index) => {
    const fromMs = fact.atMs;
    let toMs = Math.min(displayMs, flags[index + 1]?.atMs ?? displayMs);
    for (const gap of coverage?.gaps ?? []) if (gap.fromMs >= fromMs && gap.fromMs < toMs) toMs = gap.fromMs;
    return toMs > fromMs ? [{ fromMs, toMs, status: fact.status!, sourceId: fact.id, label: fact.label }] : [];
  });
}

export function comparableLaps(a: DriverState, b?: DriverState) {
  const lapsA = new Map((a.recentLaps ?? []).filter(lap => !lap.partial).map(lap => [lap.lap, lap]));
  const lapsB = new Map((b?.recentLaps ?? []).filter(lap => !lap.partial).map(lap => [lap.lap, lap]));
  return [...new Set([...lapsA.keys(), ...lapsB.keys()])].sort((left, right) => right - left).slice(0, 10).map(lap => {
    const a = lapsA.get(lap), b = lapsB.get(lap);
    const deltaSeconds = a?.duration && b?.duration && !a.deleted && !b.deleted ? a.duration - b.duration : null;
    return { lap, a, b, deltaSeconds };
  });
}
